/**
 * NdaDocument — the signed Confidentiality Agreement document rendered inline
 * (matching the prototype's `.doc` element). Pure presentational component:
 * no interactivity, just renders the agreement markup with the disclosee's
 * details filled in. British English.
 *
 * The logo + signature images are imported from `frontend/src/assets/nda/`
 * (mirrors the backend `assets/nda/` used by NdaPdfService for the PDF).
 */
import {
  CLAUSES,
  PARTIES_RECIPIENT,
  SIGNATORY,
  SIGNATORY_ROLE,
  FOOTER,
  fullName,
  fullAddress,
  type DiscloseeDetails,
} from '../types/ndaText.js';
import logoUrl from '../assets/nda/logo.png';
import signatureUrl from '../assets/nda/signature.png';

export interface NdaDocumentProps {
  /** The disclosee (inventor) details captured in the NDA form. */
  disclosee: DiscloseeDetails;
  /** DD/MM/YYYY date label (server-generated). */
  dateLabel: string;
  /** Agreement reference (e.g. "IDA-XXXX-YYYY"). */
  reference: string;
  /** ISO-8601 signed-at timestamp, formatted for display. */
  signedAt: string;
}

/** Format an ISO timestamp as "10 August 2026 at 14:30 (Europe/London)". */
function formatSignTime(iso: string): string {
  try {
    const d = new Date(iso);
    const day = d.getDate();
    const months = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];
    const tz = Intl.DateTimeFormat().resolvedOptions().timeZone;
    const hh = String(d.getHours()).padStart(2, '0');
    const mm = String(d.getMinutes()).padStart(2, '0');
    return `${day} ${months[d.getMonth()]} ${d.getFullYear()} at ${hh}:${mm} (${tz})`;
  } catch {
    return iso;
  }
}

export function NdaDocument({ disclosee, dateLabel, reference, signedAt }: NdaDocumentProps) {
  return (
    <div className="itk-nda-doc">
      <img className="itk-nda-doc-logo" src={logoUrl} alt="Innovate Design" />
      <div className="itk-nda-doc-title">Confidentiality Agreement</div>
      <div className="itk-nda-doc-meta">
        <span className="lbl">Date:</span> <span>{dateLabel}</span>
      </div>
      <div className="itk-nda-parties">
        <span className="lbl">Parties:</span>
        <div style={{ marginTop: 8 }}>{PARTIES_RECIPIENT}</div>
        <span className="and">and</span>
        <div className="itk-nda-party-locked">
          <strong>{fullName(disclosee)}</strong> of {fullAddress(disclosee)} (the Discloser)
        </div>
      </div>
      {CLAUSES.map((clause, i) => (
        <div key={i} className="itk-nda-clause">
          {clause}
        </div>
      ))}
      <div className="itk-nda-sig-block">
        <div className="itk-nda-sig-lead">Executed by {SIGNATORY}, a Director:</div>
        <div className="itk-nda-sig-done">
          <div className="itk-nda-sig-row">
            <div className="itk-nda-sig-col">
              <img className="itk-nda-sig-img" src={signatureUrl} alt="Signature" />
              <hr className="itk-nda-sig-rule" />
              <div className="itk-nda-sig-caption">
                <strong>{SIGNATORY}</strong> &mdash; {SIGNATORY_ROLE}
              </div>
            </div>
            <div className="itk-nda-esign-badge">
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2.2} strokeLinecap="round" strokeLinejoin="round">
                <path d="M9 12l2 2 4-4" />
                <path d="M21 12c0 4.97-4.03 9-9 9-2.5 0-4.77-1.02-6.4-2.66" />
                <path d="M3 12a9 9 0 0 1 15.4-6.34" />
              </svg>
              <div className="t">
                <b>Digitally signed</b> by {SIGNATORY} on behalf of Innovate Product Design Limited.
                <br />
                Signed: <span>{formatSignTime(signedAt)}</span>
                <br />
                Reference: <span className="mono">{reference}</span>
              </div>
            </div>
          </div>
        </div>
      </div>
      <div className="itk-nda-doc-footer">{FOOTER}</div>
    </div>
  );
}